import React, {FC, RefObject, useState} from 'react';
import {FormattedMessage, FormattedNumber} from 'react-intl';
import {PremiumPromoCard} from '@liveComponents/PromoCard/Premium';
import {Box} from '@components/Box';
import {ContentBox} from '@components/ContentBox';
import {Row} from '@components/Row';
import {Col} from '@components/Col';
import {Typography} from '@components/Typography';
import {PriceTable} from '@components/PriceTable';
import {LocaleEnum, Props} from 'src/types/general';
import {AdvertImportType, AdvertMessageType, Currency, OfferType} from '.cache/__types__';
import {SendMessageButton, SendMessageButtonProps} from '@pageComponents/Detail/SendMessageButton';
import {useLocale} from '@utils/locale';
import {Button} from '@components/Button';
import {FlatioModal} from '@pageComponents/Detail/FlatioModal';
import {getMortageData} from '@utils/mortage';

export type StickyBoxContentProps = {
    advert: {
        type: AdvertImportType;
        dataJson?: string;
        active: Boolean;
        offerType: OfferType;
        price: number;
        charges?: number;
        deposit?: number;
        currency: Currency;
        messageData: SendMessageButtonProps['messageData'];
        mortgageData?: {
            rate: number;
        };
    };
    onContactOwnerModalOpen: SendMessageButtonProps['onContactOwnerModalOpen'];
    sendMessageButtonRef?: RefObject<HTMLButtonElement>;
} & Props;

export const StickyBoxContent: FC<StickyBoxContentProps> = ({advert, sendMessageButtonRef, className, onContactOwnerModalOpen}) => {
    const {repayment, rate} = advert.mortgageData?.rate ? getMortageData(advert.price, advert.mortgageData.rate) : {repayment: 0, rate: 0};

    const locale = useLocale();
    const [showFlatio, setFlatio] = useState<boolean>(false);

    return (
        <Box className={className}>
            <ContentBox className="mb-5 px-5 py-6 p-xl-12">
                {advert.offerType === OfferType.Prodej && (
                    <Row as="p" className="justify-content-between align-items-baseline mb-6 mb-lg-9">
                        <Col as="span" xs="auto">
                            <Typography variant="subheadline">
                                <FormattedMessage defaultMessage="Cena" />
                            </Typography>
                        </Col>
                        <Col as="span" xs="auto">
                            <Typography as="strong" variant="h4" fontWeight="bold">
                                <FormattedNumber
                                    value={advert.price}
                                    style={'currency'}
                                    currency={advert.currency}
                                    maximumFractionDigits={0}
                                />
                            </Typography>
                        </Col>
                    </Row>
                )}

                {advert.offerType !== OfferType.Prodej && advert.type !== AdvertImportType.EChalupy && (
                    <Row as="p" className="justify-content-between align-items-baseline mb-6 mb-lg-9">
                        <Col as="span" xs="auto">
                            <Typography variant="subheadline">
                                <FormattedMessage defaultMessage={'Měsíční nájemné'} />
                            </Typography>
                        </Col>
                        <Col as="span" xs="auto">
                            <Typography as="strong" variant="h4" fontWeight="bold">
                                <FormattedNumber
                                    value={advert.price + (advert?.charges ?? 0)}
                                    style={'currency'}
                                    currency={advert.currency}
                                    maximumFractionDigits={0}
                                />
                            </Typography>
                        </Col>
                    </Row>
                )}

                {advert.offerType !== OfferType.Prodej && advert.type === AdvertImportType.EChalupy && advert.dataJson && (
                    <Row as="p" className="justify-content-between align-items-baseline mb-6 mb-lg-9">
                        <Col as="span" xs="auto">
                            <Typography variant="subheadline">{JSON.parse(advert.dataJson).cenik}</Typography>
                        </Col>
                    </Row>
                )}

                <Typography as="p" className="mb-6">
                    <SendMessageButton
                        ref={sendMessageButtonRef}
                        messageData={advert.messageData}
                        onContactOwnerModalOpen={onContactOwnerModalOpen}
                        type={advert.type}
                        provisionSaved={advert.offerType === OfferType.Pronajem ? advert.price : (advert.price * 0.04)}
                        currency={advert.currency}
                    />
                </Typography>

                {locale === LocaleEnum.cs && advert.offerType === OfferType.Prodej && advert?.mortgageData?.rate && (
                    <Typography variant="perex" as="p" className="mb-0">
                        <FormattedMessage
                            defaultMessage={
                                'Tato nemovitost již od <bold>{price}</bold> měsíčně s\u00a0roční úrokovou sazbou <bold>{rate}</bold>.'
                            }
                            values={{
                                bold: (chunks: any) => (
                                    <Typography variant="perex-lg" fontWeight="bold">
                                        {chunks}
                                    </Typography>
                                ),
                                price: (
                                    <FormattedNumber
                                        style="currency"
                                        value={repayment}
                                        maximumFractionDigits={0}
                                        currency={advert.currency}
                                    />
                                ),
                                rate: <FormattedNumber style="percent" value={rate} maximumFractionDigits={2} />,
                            }}
                        />
                    </Typography>
                )}

                {advert.type === AdvertImportType.Flatio && (
                    <Typography variant="perex" as="p" className="mb-2">
                        <FormattedMessage defaultMessage={'Tuto nabídku můžete rezervovat na Flatio.'} />
                    </Typography>
                )}

                {advert.type === AdvertImportType.Flatio && (
                    <>
                        <Button variant={'link'} onClick={() => setFlatio(true)} className={'mb-5'}>
                            <FormattedMessage defaultMessage={'Co je Flatio?'} />
                        </Button>
                        <FlatioModal show={showFlatio} onHide={() => setFlatio(false)} />
                    </>
                )}

                {advert.offerType !== OfferType.Prodej && advert.type !== AdvertImportType.EChalupy && (
                    <ContentBox background="grey-light" className="p-5 p-xl-6" rounded="sm">
                        <PriceTable>
                            <tbody>
                                <tr>
                                    <th>
                                        <FormattedMessage defaultMessage={'Základní nájemné'} />
                                    </th>
                                    <td>
                                        <strong>
                                            <FormattedNumber
                                                value={advert.price}
                                                style={'currency'}
                                                currency={advert.currency}
                                                maximumFractionDigits={0}
                                            />
                                        </strong>
                                    </td>
                                </tr>
                                {typeof advert.charges !== 'undefined' && (
                                    <tr>
                                        <th>
                                            <FormattedMessage defaultMessage={'Poplatky za energie'} />
                                        </th>
                                        <td>
                                            <strong>
                                                <FormattedNumber
                                                    value={advert.charges}
                                                    style="currency"
                                                    currency={advert.currency}
                                                    maximumFractionDigits={0}
                                                />
                                            </strong>
                                        </td>
                                    </tr>
                                )}
                                {typeof advert.deposit !== 'undefined' &&
                                    !(advert.type === AdvertImportType.Flatio && advert.deposit === 0) && (
                                        <tr>
                                            <th>
                                                <FormattedMessage defaultMessage="Vratná kauce" />
                                            </th>
                                            <td>
                                                <strong>
                                                    <FormattedNumber
                                                        value={advert.deposit}
                                                        style="currency"
                                                        currency={advert.currency}
                                                        maximumFractionDigits={0}
                                                    />
                                                </strong>
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        </PriceTable>
                    </ContentBox>
                )}
            </ContentBox>

            {advert.messageData.messageType === AdvertMessageType.RequirePremium && (
                <div
                    style={{
                        maxWidth: '40rem',
                    }}
                >
                    <PremiumPromoCard currency={advert.currency} priceSaved={advert.offerType === OfferType.Pronajem ? advert.price : (advert.price * 0.04)} />
                </div>
            )}
        </Box>
    );
};
